import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { ShieldAlert, X, Ban } from "lucide-react";
import Modal from "@/crm/components/Modal";

export function ExclusionBanner({ company }) {
  const exc = company.company_exclusion;
  if (!company.excluded && !exc) return null;
  return (
    <div data-testid="exclusion-banner" className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-4 flex items-start gap-3">
      <ShieldAlert size={22} className="text-rose-600 shrink-0 mt-0.5" />
      <div>
        <div className="font-heading font-bold text-rose-700 tracking-tight">🚫 NE PLUS CONTACTER</div>
        <div className="text-sm text-rose-600 mt-1">
          Motif : <strong>{exc?.reason || "—"}</strong> · Canaux bloqués : {(exc?.blocked_channels || ["ALL"]).join(", ")}
          {exc?.created_at && <> · Depuis le {new Date(exc.created_at).toLocaleDateString("fr-FR")}</>}
          {exc?.created_by_name && <> · par {exc.created_by_name}</>}
        </div>
      </div>
    </div>
  );
}

export function ExclusionButton({ company, onSaved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid="ne-plus-contacter-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-rose-300 text-rose-600 text-sm hover:bg-rose-50">
        <Ban size={15} /> Ne plus contacter
      </button>
      {open && <ExclusionModal company={company} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onSaved(); }} />}
    </>
  );
}

function ExclusionModal({ company, onClose, onSaved }) {
  const [constants, setConstants] = useState({ reasons: [], channels: [] });
  const [type, setType] = useState("COMPANY");
  const [contactId, setContactId] = useState("");
  const [reason, setReason] = useState("Demande explicite du prospect");
  const [channels, setChannels] = useState(["ALL"]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    api.get("/exclusions/constants").then(({ data }) => {
      setConstants(data);
      if (data.reasons?.length) setReason(data.reasons[0]);
    }).catch(() => {});
  }, []);

  const toggleChannel = (code) => {
    if (code === "ALL") return setChannels(["ALL"]);
    setChannels((prev) => {
      const next = prev.filter((c) => c !== "ALL");
      return next.includes(code) ? next.filter((c) => c !== code) : [...next, code];
    });
  };

  const submit = async () => {
    if (type === "CONTACT" && !contactId) return toast.error("Sélectionnez un contact");
    try {
      await api.post(`/companies/${company.id}/exclusions`, {
        type, contact_id: type === "CONTACT" ? contactId : null,
        reason, blocked_channels: channels.length ? channels : ["ALL"], notes,
      });
      toast.success("Opposition enregistrée");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal onClose={onClose} testid="exclusion-modal" label="Ne plus contacter">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-lg text-rose-700 flex items-center gap-2"><Ban size={18} /> Ne plus contacter</h3>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Cette action empêchera les futures actions de prospection selon les canaux sélectionnés.</p>

      <label className="text-xs font-medium text-slate-500">Niveau</label>
      <div className="grid grid-cols-2 gap-2 mt-1 mb-3">
        {[["COMPANY", "Entreprise entière"], ["CONTACT", "Un contact"]].map(([v, l]) => (
          <button key={v} data-testid={`excl-type-${v}`} onClick={() => setType(v)}
            className={`text-sm rounded-lg py-2 border ${type === v ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {type === "CONTACT" && (
        <div className="mb-3">
          <label className="text-xs font-medium text-slate-500">Contact</label>
          <select data-testid="excl-contact-select" value={contactId} onChange={(e) => setContactId(e.target.value)} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
            <option value="">Sélectionner…</option>
            {(company.contacts || []).map((ct) => <option key={ct.id} value={ct.id}>{ct.prenom} {ct.nom}</option>)}
          </select>
        </div>
      )}

      <label className="text-xs font-medium text-slate-500">Motif</label>
      <select data-testid="excl-reason-select" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full mt-1 mb-3 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
        {constants.reasons.map((r) => <option key={r}>{r}</option>)}
      </select>

      <label className="text-xs font-medium text-slate-500">Canaux bloqués</label>
      <div className="flex flex-wrap gap-2 mt-1 mb-3">
        {constants.channels.map((c) => (
          <button key={c.code} data-testid={`excl-channel-${c.code}`} onClick={() => toggleChannel(c.code)}
            className={`text-xs rounded-lg px-3 py-1.5 border ${channels.includes(c.code) ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-600"}`}>{c.label}</button>
        ))}
      </div>

      <textarea data-testid="excl-notes" placeholder="Note (optionnel)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm mb-4" />
      <button data-testid="excl-submit" onClick={submit} className="w-full py-2.5 rounded-lg bg-rose-600 text-white text-sm hover:bg-rose-700">Enregistrer l'opposition</button>
    </Modal>
  );
}
